import { useState, useEffect, useCallback } from 'react';
import { Folder, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { accessoEsternoPublic } from '../utils/api';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';

var TIPO_LABELS = {
  preventivo: 'Preventivo', contratto: 'Contratto', fattura: 'Fattura',
  foto: 'Foto', ddt: 'DDT', bolla: 'Bolla', certificato: 'Certificato',
  collaudo: 'Collaudo', planimetria: 'Planimetria', sal: 'SAL',
  computo: 'Computo metrico', corrispondenza: 'Corrispondenza',
  pdf_generico: 'PDF generico', altro: 'Altro',
};

var container = {
  minHeight: '100vh',
  background: 'var(--chip)',
  fontFamily: 'Inter, Arial, sans-serif',
};

var topBar = {
  background: 'var(--brand)',
  padding: '14px 24px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

var content = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '24px 20px',
};

var cardStyle = {
  background: 'var(--ds-bg)',
  borderRadius: 'var(--radius-card)',
  boxShadow: CARD_SHADOW,
  overflow: 'hidden',
};

var listItem = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 20px',
  borderBottom: '0.5px solid var(--ds-border)',
  cursor: 'pointer',
  transition: 'background 100ms',
  minHeight: 44,
};

var btnOutline = {
  padding: '8px 16px', borderRadius: 'var(--radius-base)',
  border: '0.5px solid rgba(255,255,255,0.4)',
  background: 'transparent', color: '#fff',
  fontSize: 12, fontWeight: 500, cursor: 'pointer',
  minHeight: 44,
};

function FolderIcon() {
  return <Folder size={18} strokeWidth={1.5} color="var(--brand)" />;
}

function FileIcon() {
  return <FileText size={16} strokeWidth={1.5} color="var(--brand)" />;
}

function BackIcon() {
  return <ChevronLeft size={16} strokeWidth={2} />;
}

export default function AccesoEsternoPortale(props) {
  var token = props.token;
  var utente = props.utente;
  var onLogout = props.onLogout;

  var [vista, setVista] = useState('cartelle'); // cartelle, documenti, dettaglio
  var [cartelle, setCartelle] = useState([]);
  var [documenti, setDocumenti] = useState([]);
  var [cartellaAttiva, setCartellaAttiva] = useState(null);
  var [docAttivo, setDocAttivo] = useState(null);
  var [loading, setLoading] = useState(false);

  var fetchCartelle = useCallback(function() {
    setLoading(true);
    accessoEsternoPublic.cartelle(token)
      .then(function(res) { setCartelle(res.data || []); })
      .catch(function() { setCartelle([]); })
      .finally(function() { setLoading(false); });
  }, [token]);

  useEffect(function() {
    fetchCartelle();
  }, [fetchCartelle]);

  var apriCartella = function(c) {
    setCartellaAttiva(c);
    setLoading(true);
    accessoEsternoPublic.documentiCartella(token, c.id)
      .then(function(res) { setDocumenti(res.data || []); setVista('documenti'); })
      .catch(function() { setDocumenti([]); setVista('documenti'); })
      .finally(function() { setLoading(false); });
  };

  var apriDocumento = function(d) {
    setLoading(true);
    accessoEsternoPublic.documento(token, d.id)
      .then(function(res) { setDocAttivo(res.data); setVista('dettaglio'); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  };

  var scaricaDocumento = function(docId) {
    accessoEsternoPublic.download(token, docId)
      .then(function(res) {
        if (res.data?.file_url) window.open(res.data.file_url, '_blank');
      })
      .catch(function() {});
  };

  var tornaCartelle = function() {
    setVista('cartelle');
    setCartellaAttiva(null);
    setDocumenti([]);
  };

  var tornaDocumenti = function() {
    setVista('documenti');
    setDocAttivo(null);
  };

  var fmtData = function(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('it-IT');
  };

  return (
    <div style={container}>
      {/* Top bar */}
      <div style={topBar}>
        <div>
          <span style={{ color: '#fff', fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)' }}>mAIstro</span>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginLeft: 12, fontWeight: 400 }}>
            {utente?.nome} &middot; {utente?.tenant_nome}
          </span>
        </div>
        <button onClick={onLogout} style={btnOutline}>Esci</button>
      </div>

      <div style={content}>
        {/* CARTELLE */}
        {vista === 'cartelle' && (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', margin: '0 0 16px', fontFamily: 'var(--font-display)' }}>Le tue cartelle</h2>
            {loading ? (
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Caricamento...</p>
            ) : cartelle.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Nessuna cartella assegnata.</p>
            ) : (
              <div style={cardStyle}>
                {cartelle.map(function(c) {
                  return (
                    <div
                      key={c.id}
                      style={listItem}
                      onClick={function() { apriCartella(c); }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--ds-bg)'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FolderIcon />
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{c.nome}</div>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', fontWeight: 400 }}>{c.tipo} &middot; {c.permessi}</div>
                        </div>
                      </div>
                      <ChevronRight size={14} strokeWidth={2} color="var(--ds-muted)" />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* DOCUMENTI IN CARTELLA */}
        {vista === 'documenti' && (
          <>
            <button
              onClick={tornaCartelle}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--brand)', fontWeight: 500, marginBottom: 12, padding: 0, minHeight: 44 }}
            >
              <BackIcon /> Torna alle cartelle
            </button>
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', margin: '0 0 16px', fontFamily: 'var(--font-display)' }}>
              {cartellaAttiva?.nome || 'Cartella'}
            </h2>
            {loading ? (
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Caricamento...</p>
            ) : documenti.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Nessun documento in questa cartella.</p>
            ) : (
              <div style={cardStyle}>
                {documenti.map(function(d) {
                  return (
                    <div
                      key={d.id}
                      style={listItem}
                      onClick={function() { apriDocumento(d); }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--ds-bg)'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FileIcon />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{d.nome_file}</div>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', fontWeight: 400 }}>
                            {TIPO_LABELS[d.tipo] || d.tipo} &middot; {d.dimensione_leggibile} &middot; {fmtData(d.data_upload)}
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={14} strokeWidth={2} color="var(--ds-muted)" />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* DETTAGLIO DOCUMENTO */}
        {vista === 'dettaglio' && docAttivo && (
          <>
            <button
              onClick={tornaDocumenti}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--brand)', fontWeight: 500, marginBottom: 12, padding: 0, minHeight: 44 }}
            >
              <BackIcon /> Torna ai documenti
            </button>
            <div style={Object.assign({}, cardStyle, { padding: '24px' })}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <FileIcon />
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{docAttivo.nome_file}</h2>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ds-muted)', fontWeight: 400 }}>
                    {TIPO_LABELS[docAttivo.tipo] || docAttivo.tipo} &middot; {docAttivo.dimensione_leggibile}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={function() { scaricaDocumento(docAttivo.id); }}
                  style={{
                    width: '100%', padding: '12px 20px', borderRadius: 'var(--radius-base)',
                    border: 'none', background: 'var(--brand)',
                    color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', minHeight: 44,
                  }}
                >
                  Scarica documento
                </button>
                {docAttivo.file_url && (
                  <button
                    onClick={function() { window.open(docAttivo.file_url, '_blank'); }}
                    style={{
                      width: '100%', padding: '12px 20px', borderRadius: 'var(--radius-base)',
                      border: '0.5px solid var(--ds-border)', background: 'var(--ds-bg)',
                      color: 'var(--ds-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer', minHeight: 44,
                    }}
                  >
                    Visualizza nel browser
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
